import React, { useEffect } from 'react'
import { Modal } from '../../components/Modal/Modal'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { InviteResponse, useDeleteInviteMutation } from '../../store/auth/auth'
import { planApi } from '../../store/plan/plan'
import { addToast } from '../../store/toast/toastSlice'
import { getRoleGroupForRoles } from './InviteRoleGroups'
import { seatTypeLabels } from './roleChangeSeatImpact'

interface DeleteInviteModalProps {
  invite?: InviteResponse
  show: boolean
  onHide: () => void
}

const freedSeatText = (invite: InviteResponse): string => {
  const seatType = getRoleGroupForRoles(invite.user.roleIds)?.type
  return seatType ? `1 ${seatTypeLabels[seatType]} seat becomes` : '1 seat becomes'
}

export const DeleteInviteModal: React.FC<DeleteInviteModalProps> = ({ invite, show, onHide }) => {
  const accountId = useAppSelector(selectAccountId)
  const [deleteInvite, deleteInviteResult] = useDeleteInviteMutation()
  const dispatch = useAppDispatch()
  const isPending = invite?.state !== 'expired'

  const handleDeleteInvite = async () => {
    if (invite) {
      deleteInvite({ accountId: accountId, inviteId: invite.id })
      dispatch(planApi.util.invalidateTags(['BillingCustomer']))
    }
  }

  useEffect(() => {
    if (deleteInviteResult.isSuccess) {
      dispatch(addToast({ variant: 'success', title: isPending ? 'Invitation revoked' : 'Expired invitation revoked' }))
      onHide()
    }
  }, [deleteInviteResult.isSuccess, isPending, dispatch, onHide])

  if (!invite) {
    return null
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      header={<h4>{isPending ? 'Revoke pending invitation?' : 'Delete expired invitation?'}</h4>}
      headerBodySeparator
      body={<div className='paragraph-medium'>The invitation will be cancelled and {freedSeatText(invite)} available.</div>}
      showFooterActions={true}
      onFooterPrimaryAction={handleDeleteInvite}
      footerPrimaryActionLoading={deleteInviteResult.isLoading}
      footerPrimaryActionTitle={isPending ? 'Revoke' : 'Delete'}
      footerPrimaryActionButtonVariant='danger'
    ></Modal>
  )
}
